import React, {memo, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {Button, Popconfirm} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {Virtuoso} from 'react-virtuoso';

import {MethodTag} from '../method-tag';
import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';

// Flat list of API paths, virtualized via react-virtuoso.
//
// The initial mount was a second-long pause at OTM-scale (~3100 paths) even
// with content-visibility, because content-visibility only defers browser
// paint — React still reconciles and mounts all rows. Virtuoso renders only
// the visible slice (+ a small overscan), so first mount is ~30 rows
// instead of 3000+.
//
// No hierarchy: paths render flat (a/b/{c}/d per row). Tree nav adds no
// navigation value for URLs — you know the endpoint you're after.

const SUPPORTED_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'trace'];

function buildEntries(paths) {
    if (!paths) return [];
    return paths.map(({key, path, spec}) => {
        const methods = Object.keys(spec || {}).filter((m) => SUPPORTED_METHODS.indexOf(m) !== -1);
        return {
            key,
            path,
            method: methods.length === 1 ? methods[0] : methods,
        };
    });
}

const ApiRow = memo(function ApiRow({entry, isActive, href, onNavigate, onDelete, editMode}) {
    return (
        <div className={`apiList__row${isActive ? ' is-active' : ''}`}>
            <span className="apiList__method">
                <MethodTag method={entry.method} />
            </span>
            <Link className="apiList__path" disabled={editMode} onClick={onNavigate} to={href}>
                {entry.path}
            </Link>
            <Popconfirm okType="default" title={`Delete interface ${entry.path} ?`} onConfirm={() => onDelete(entry)}>
                <Button
                    className="apiList__delete"
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                />
            </Popconfirm>
        </div>
    );
});

export function ApiIndex({currentHash, filter, onDelete, params, checkMobileView}) {
    const {dispatch} = useContext(AppContext);
    const {state} = useContext(OpenApiContext);

    const [interfaces, setInterfaces] = useState([]);

    useEffect(() => {
        if (state.interfaces) setInterfaces(state.interfaces(filter));
    }, [state.interfaces, filter, currentHash]);

    const entries = useMemo(() => buildEntries(interfaces), [interfaces]);

    const onNavigate = useCallback(() => {
        if (checkMobileView) checkMobileView();
        dispatch({type: 'clear-breadcrumb'});
    }, [checkMobileView, dispatch]);

    const onDeleteEntry = useCallback(
        (entry) => {
            onDelete({...entry, path: entry.path});
        },
        [onDelete],
    );

    const activeKey = params && params.view === 'interfaces' ? params.intfKey : null;

    const itemContent = useCallback(
        (index) => {
            const entry = entries[index];
            const href = `/${params.env}/standards/${params.category}/${params.key}/${params.version}/interfaces/${entry.key}`;
            return (
                <ApiRow
                    entry={entry}
                    isActive={activeKey === entry.key}
                    href={href}
                    onNavigate={onNavigate}
                    onDelete={onDeleteEntry}
                    editMode={state.editMode}
                />
            );
        },
        [entries, params, activeKey, onNavigate, onDeleteEntry, state.editMode],
    );

    return (
        <div className="apiList">
            <Virtuoso
                className="apiList__virtuoso"
                totalCount={entries.length}
                itemContent={itemContent}
                computeItemKey={(index) => entries[index]?.key || index}
                increaseViewportBy={{top: 200, bottom: 200}}
                overscan={10}
            />
        </div>
    );
}
